import type { IntentSignal } from "../types";

/**
 * Formats location object into displayable string
 * @example formatLocation(location) => "Las Cruces, New Mexico, United States"
 * @example formatLocation(location) => "California, United States" (no city)
 * @example formatLocation(null) => ""
 */
export const formatLocation = (location: IntentSignal["location"]): string => {
  if (!location) return "";

  const city = location.cities?.[0];
  const state = location.state?.name;
  const country = location.state?.country?.name;

  // Filter out empty/null values and join with comma-space
  return [city, state, country].filter(Boolean).join(", ");
};

/**
 * Determines trend direction based on percentage threshold
 * @param trend - Percentage value (e.g., 15.5 means 15.5% increase)
 * @returns "up" if > 10%, "down" if < -10%, "flat" otherwise
 */
export const getTrendDirection = (trend: number): "up" | "down" | "flat" => {
  if (trend > 10) return "up";
  if (trend < -10) return "down";
  return "flat";
};

/**
 * Generates human-readable trend description for tooltip
 * @example getTrendDescription(15.5, "CRM Systems") =>
 *   "We're currently detecting a 15.5% increase in activity for CRM Systems"
 * @example getTrendDescription(0, "Analytics") =>
 *   "We're currently not detecting any changes in intent activity for Analytics"
 */
export const getTrendDescription = (
  trend: number,
  topicName: string
): string => {
  if (trend === 0) {
    return `We're currently not detecting any changes in intent activity for ${topicName}`;
  }

  const direction = trend > 0 ? "increase" : "decrease";
  const value = Math.abs(Math.round(trend * 100) / 100);

  return `We're currently detecting a ${value}% ${direction} in activity for ${topicName}`;
};
